import { 
    Box, 
    IconButton, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Tooltip,
    Chip
} from "@mui/material";
import { Edit, History, ManageAccounts, ContentCopy } from "@mui/icons-material";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminPackageDto } from "../../api/models/PackageDto";
import { useSelector } from "react-redux";
import { selectHeiUser } from "../../../../Auth/store/accountSlice";

interface AdminPackagesGridProps {
    packages: AdminPackageDto[];
    reloadData: () => Promise<void>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    onClonePackage: (packageId: string) => Promise<string>;
}

const AdminPackagesGrid = ({ packages, reloadData, setIsLoading, onClonePackage }: AdminPackagesGridProps): JSX.Element => {
    const navigate = useNavigate();

    const heiUser = useSelector(selectHeiUser);

    const isAdmin = useMemo(
        () => heiUser.roles.some((r) => r === "Admin"),
        [heiUser]
    );
    const handleEdit = (packageId: string) => {
        navigate(`/admin/packages/${packageId}/edit`);
    };

    const handleHistory = (packageId: string) => {
        navigate(`/admin/packages/${packageId}/history`);
    };

    const handleManageAccess = (packageId: string) => {
        navigate(`/admin/packages/${packageId}/access`);
    };

    const handleClone = async (packageId: string) => {
        const newPackageId = await onClonePackage(packageId);
        navigate(`/admin/packages/${newPackageId}/edit`);
    };

    return (
        <div data-testid="admin-packages-grid">
            <Box pl="80px" pt="24px" height="100%">
                <TableContainer
                    component={Paper}
                    sx={{ width: "92%", pb: 0, mb: "70px", mt: "10px" }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: "25%" }}>
                                    <b>Name</b>
                                </TableCell>
                                <TableCell sx={{ width: "40%" }}>
                                    <b>Description</b>
                                </TableCell>
                                <TableCell sx={{ width: "15%" }}>
                                    <b>Published</b>
                                </TableCell>
                                <TableCell sx={{ width: "20%" }}>
                                    <b>Actions</b>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {packages.map((pkg: AdminPackageDto) => (
                                <TableRow key={pkg.id}>
                                    <TableCell>{pkg.name}</TableCell>
                                    <TableCell>{pkg.description}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={pkg.published ? "Published" : "Draft"} 
                                            color={pkg.published ? "success" : "default"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit Package">
                                            <IconButton 
                                                onClick={() => handleEdit(pkg.id)}
                                                size="small"
                                                color="primary"
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        {/* {pkg.canManageAccess && ( */}
                                            <Tooltip title="Manage Access">
                                                <IconButton 
                                                    onClick={() => handleManageAccess(pkg.id)}
                                                    size="small"
                                                    color="secondary"
                                                >
                                                    <ManageAccounts />
                                                </IconButton>
                                            </Tooltip>
                                        {/* )} */}
                                        <Tooltip title="View History">
                                            <IconButton 
                                                onClick={() => handleHistory(pkg.id)}
                                                size="small"
                                                color="default"
                                            >
                                                <History />
                                            </IconButton>
                                        </Tooltip>
                                        {isAdmin && <Tooltip title="Clone Package">
                                            <IconButton 
                                                onClick={() => handleClone(pkg.id)}
                                                size="small"
                                                color="info"
                                            >
                                                <ContentCopy />
                                            </IconButton>
                                        </Tooltip>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </div>
    );
};

export default AdminPackagesGrid;